import {
	CheckCircleFilled,
	ClockCircleOutlined,
	CloseCircleOutlined,
	DownOutlined,
	LoadingOutlined,
	MinusCircleOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import OWButton from '../../../components/ow_button/OWButton';
import { cn } from '../../../utils/ClassNamesUtils';
import { useOscarTheme } from '../../context/OscarRoleContext';
import {
	AGENT_TEAM_REQUEST_TOOL,
	AgentApprovalsResult,
	AgentTranscriptEntry,
} from '../../../types/agentHarness';
import { humanizeEnumValue, humanizeFieldLabel, humanizeToolName } from '../../utils/OscarLabels';
import { approvalRowOutcomeLabel } from '../../utils/OscarApprovalOutcome';
import { OscarApprovalArgs } from './OscarApprovalCard';
import OscarTeamRequestCard from './OscarTeamRequestCard';

type ApprovalEntry = Extract<AgentTranscriptEntry, { kind: 'approval' }>;

interface OscarApprovalBatchCardProps {
	/** Every approval of one model step that has arrived so far, in order. */
	approvals: ApprovalEntry[];
	/** How many approvals the step produced (`batchSize`); the card is complete at that many. */
	batchSize: number;
	/** One card's own answer — a row, or a team request. */
	onResolve?: (approvalId: string, approved: boolean) => void | Promise<boolean>;
	/** One answer for many, in one request (`useAgentChat().resolveApprovals`). */
	onResolveMany?: (ids: string[], approved: boolean) => Promise<AgentApprovalsResult>;
	className?: string;
}

/** Rows shown before "Show all". */
export const BATCH_ROW_CAP = 5;

/** Argument names that say what a record is called, best first. */
const NAME_KEYS = ['name', 'title', 'subject', 'summary', 'displayName', 'label'];
const NAME_SUFFIX = /(name|title|subject|summary)$/i;

/** Long values make a poor one-line title. */
const TITLE_VALUE_MAX = 80;

const isShortText = (value: unknown): value is string =>
	typeof value === 'string' &&
	value.trim() !== '' &&
	value.length <= TITLE_VALUE_MAX &&
	value.indexOf('\n') === -1;

/**
 * One line naming what a row would write: the record's own name if the call
 * carries one, then the names the harness learned for its ids ("Store 12"),
 * and failing both its first short field. Display only — the expanded row
 * shows the exact arguments.
 */
export const approvalRowTitle = (
	args: unknown,
	labels: Record<string, string> | undefined,
	index: number
): string => {
	const parts: string[] = [];
	const fields =
		args && typeof args === 'object' && !Array.isArray(args)
			? (args as Record<string, unknown>)
			: null;
	if (fields) {
		const exact = NAME_KEYS.filter((key) => isShortText(fields[key]));
		const suffixed = Object.keys(fields).filter(
			(key) => NAME_SUFFIX.test(key) && isShortText(fields[key])
		);
		const nameKey = exact[0] || suffixed[0];
		if (nameKey) {
			parts.push((fields[nameKey] as string).trim());
		}
	}
	if (labels) {
		Object.keys(labels).forEach((key) => {
			const label = labels[key];
			if (label && parts.indexOf(label) === -1) {
				parts.push(label);
			}
		});
	}
	if (!parts.length && fields) {
		const firstShort = Object.keys(fields).find(
			(key) =>
				isShortText(fields[key]) ||
				typeof fields[key] === 'number' ||
				typeof fields[key] === 'boolean'
		);
		if (firstShort) {
			parts.push(
				`${humanizeFieldLabel(firstShort)} ${humanizeEnumValue(String(fields[firstShort]))}`
			);
		}
	}
	return parts.length ? parts.join(' · ') : `Item ${index + 1}`;
};

type RowStatus = 'deciding' | 'pending' | 'approved' | 'rejected' | 'not_run';

const rowStatus = (approval: ApprovalEntry, locked: boolean): RowStatus => {
	if (!approval.resolved) {
		return locked ? 'deciding' : 'pending';
	}
	if (approval.approved) {
		return 'approved';
	}
	return approval.reason === 'denied' ? 'rejected' : 'not_run';
};

const ROW_ICONS: Record<RowStatus, React.ReactNode> = {
	deciding: <LoadingOutlined className="text-character-secondary" />,
	pending: <ClockCircleOutlined className="text-character-secondary" />,
	approved: <CheckCircleFilled className="text-[#52c41a]" />,
	rejected: <CloseCircleOutlined className="text-character-danger" />,
	not_run: <MinusCircleOutlined className="text-character-secondary" />,
};

const ROW_STATUS_TEXT: Record<RowStatus, string> = {
	deciding: 'Sending…',
	pending: 'Needs approval',
	approved: 'Approved',
	rejected: 'Rejected',
	not_run: 'Not run',
};

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/**
 * Every approval one model step asked for, as one card answered with one click.
 *
 * Asking for 24 tickets queued 24 cards in a horizontal scroller, and nobody
 * clicked through them before the approvals expired — so 24 writes the person
 * wanted never ran. One card lists them a line each, and "Approve all" answers
 * the whole step in one request.
 *
 * "All" means what the card shows: the buttons wait until every approval of the
 * step has arrived (`batchSize`), and the request names exactly the ids on
 * screen, so one still arriving is never approved by a click made before it.
 *
 * Destructive writes take a second, explicit confirmation before a bulk
 * approve. Team requests stay their own decision — they share what the customer
 * asked with OpenWrench staff — so they keep their own card and are never swept
 * into a bulk answer.
 */
export const OscarApprovalBatchCard = ({
	approvals,
	batchSize,
	onResolve,
	onResolveMany,
	className,
}: OscarApprovalBatchCardProps) => {
	const theme = useOscarTheme();
	// Per id, as in the queue: a bulk answer can partly fail, and only the ids
	// that did not land may unlock — a batch-wide flag cleared on failure would
	// reopen ones whose decision is already on its way.
	const [lockedIds, setLockedIds] = useState<{ [approvalId: string]: true }>({});
	const [bulkInFlight, setBulkInFlight] = useState(false);
	const [expandedIds, setExpandedIds] = useState<{ [approvalId: string]: true }>({});
	const [showAll, setShowAll] = useState(false);
	const [confirmingDestructive, setConfirmingDestructive] = useState(false);

	const writes = approvals.filter((approval) => approval.toolName !== AGENT_TEAM_REQUEST_TOOL);
	const teamRequests = approvals.filter(
		(approval) => approval.toolName === AGENT_TEAM_REQUEST_TOOL
	);
	// Complete when every approval of the step has arrived, or when `hello` has
	// said that the ones on screen are all that is left of it.
	const complete =
		approvals.length >= batchSize || approvals.some((approval) => !!approval.batchComplete);
	const expectedWrites = complete
		? writes.length
		: Math.max(writes.length, batchSize - teamRequests.length);
	const pendingWrites = writes.filter((approval) => !approval.resolved);
	const bulkTargets = pendingWrites.filter((approval) => !lockedIds[approval.id]);
	const destructiveTargets = bulkTargets.filter((approval) => approval.destructive);
	const anyDestructive = writes.some((approval) => approval.destructive);
	const toolNames = writes.map((approval) => approval.toolName);
	const sameTool = toolNames.length > 0 && toolNames.every((name) => name === toolNames[0]);
	const firstRationale = writes.length ? writes[0].rationale : undefined;
	const sharedRationale =
		firstRationale && writes.every((approval) => approval.rationale === firstRationale)
			? firstRationale
			: undefined;
	const visibleWrites = showAll ? writes : writes.slice(0, BATCH_ROW_CAP);

	const lock = (ids: string[]) =>
		setLockedIds((current) => {
			const next = { ...current };
			ids.forEach((id) => {
				next[id] = true;
			});
			return next;
		});
	const unlock = (ids: string[]) =>
		setLockedIds((current) => {
			const next = { ...current };
			ids.forEach((id) => {
				delete next[id];
			});
			return next;
		});

	const resolveOne = (approvalId: string, approved: boolean): Promise<boolean> => {
		if (!onResolve || lockedIds[approvalId]) {
			return Promise.resolve(false);
		}
		lock([approvalId]);
		return Promise.resolve(onResolve(approvalId, approved)).then(
			(landed) => {
				if (landed === false) {
					unlock([approvalId]);
					return false;
				}
				return true;
			},
			() => {
				unlock([approvalId]);
				return false;
			}
		);
	};

	const resolveAll = (approved: boolean) => {
		setConfirmingDestructive(false);
		if (!complete || bulkInFlight || !bulkTargets.length || (!onResolveMany && !onResolve)) {
			return;
		}
		const ids = bulkTargets.map((approval) => approval.id);
		lock(ids);
		setBulkInFlight(true);
		const run: Promise<AgentApprovalsResult> = onResolveMany
			? onResolveMany(ids, approved)
			: Promise.all(
					ids.map((id) =>
						Promise.resolve(onResolve(id, approved)).then(
							(landed) => (landed === false ? id : ''),
							() => id
						)
					)
			  ).then((failed) => ({
					resolved: [],
					missing: [],
					failed: failed.filter(Boolean),
			  }));
		// Unlocked only where the answer did not land: the stream retires the
		// rest, and re-enabling them would briefly offer a choice already made.
		run
			.then(
				(result) => unlock(result.failed),
				() => unlock(ids)
			)
			.then(() => setBulkInFlight(false));
	};

	const approveAll = () => {
		// A bulk approve that includes a destructive write asks twice.
		if (destructiveTargets.length && !confirmingDestructive) {
			setConfirmingDestructive(true);
			return;
		}
		resolveAll(true);
	};

	const toggleRow = (approvalId: string) =>
		setExpandedIds((current) => {
			const next = { ...current };
			if (next[approvalId]) {
				delete next[approvalId];
			} else {
				next[approvalId] = true;
			}
			return next;
		});

	const approvedCount = writes.filter((approval) => approval.resolved && approval.approved).length;
	// A card the harness no longer held may have been approved elsewhere and run,
	// so it is counted on its own, never as "not run".
	const missingCount = writes.filter(
		(approval) => approval.resolved && !approval.approved && approval.reason === 'missing'
	).length;
	const notRunCount = writes.filter(
		(approval) => approval.resolved && !approval.approved && approval.reason !== 'missing'
	).length;
	let progress: string;
	if (!complete) {
		progress = `Loading ${approvals.length} of ${batchSize}…`;
	} else if (!pendingWrites.length) {
		progress = [
			approvedCount ? `${approvedCount} approved` : '',
			notRunCount ? `${notRunCount} not run` : '',
			missingCount ? `${missingCount} no longer pending` : '',
		]
			.filter(Boolean)
			.join(' · ');
	} else if (pendingWrites.length < writes.length) {
		progress = `${pendingWrites.length} of ${writes.length} still need approval`;
	} else {
		progress = '';
	}

	return (
		<div
			className={cn(
				'flex w-full flex-col gap-xs rounded p-xs',
				anyDestructive
					? 'border-2 border-solid border-character-danger bg-character-dangerBg'
					: null,
				anyDestructive ? null : theme.surface,
				anyDestructive ? null : theme.stroke,
				className
			)}
			data-testid="oscar-approval-batch"
		>
			{writes.length || !complete ? (
				<>
					<span className="bodymedium text-grey-10">
						{anyDestructive ? 'Confirm destructive actions' : 'Approval needed'}
					</span>
					<span className="bodyregular text-grey-9" title={sameTool ? toolNames[0] : undefined}>
						{sameTool ? (
							<>
								{`Oscar wants to run `}
								<span className="bodymedium">{humanizeToolName(toolNames[0])}</span>
								{` × ${expectedWrites}`}
							</>
						) : (
							`Oscar wants to make ${plural(expectedWrites, 'change', 'changes')}`
						)}
					</span>
					{sharedRationale ? (
						<span className="bodyregular break-words text-grey-9">
							<span className="bodymedium">Why: </span>
							{sharedRationale}
						</span>
					) : null}
					{progress ? (
						<span className="footnoteregular text-character-secondary">{progress}</span>
					) : null}
				</>
			) : null}

			{writes.length ? (
				<ul className="m-0 flex list-none flex-col gap-0 overflow-hidden rounded bg-white p-0">
					{visibleWrites.map((approval) => {
						const index = writes.indexOf(approval);
						const status = rowStatus(approval, !!lockedIds[approval.id]);
						const expanded = !!expandedIds[approval.id];
						const statusText = approval.resolved
							? approvalRowOutcomeLabel(approval.approved, approval.reason)
							: ROW_STATUS_TEXT[status];
						return (
							<li
								key={approval.id}
								data-approval-id={approval.id}
								data-status={status}
								className="flex flex-col border-0 border-b border-solid border-grey-4 last:border-b-0"
							>
								<button
									type="button"
									aria-expanded={expanded}
									onClick={() => toggleRow(approval.id)}
									className="flex w-full cursor-pointer items-start gap-xs border-0 bg-transparent px-xs py-2xs text-left"
								>
									<span
										className="mt-[3px] flex shrink-0"
										role="img"
										aria-label={ROW_STATUS_TEXT[status]}
									>
										{ROW_ICONS[status]}
									</span>
									<span className="flex min-w-0 flex-1 flex-col">
										<span className="bodyregular truncate text-grey-9">
											{!sameTool ? (
												<span className="bodymedium">{`${humanizeToolName(
													approval.toolName
												)}: `}</span>
											) : null}
											{approvalRowTitle(approval.args, approval.labels, index)}
										</span>
										{!sharedRationale && approval.rationale ? (
											<span className="footnoteregular break-words text-character-secondary">
												{approval.rationale}
											</span>
										) : null}
									</span>
									<span className="footnoteregular shrink-0 text-character-secondary">
										{approval.destructive && !approval.resolved ? (
											<span className="text-character-danger">Destructive · </span>
										) : null}
										{statusText}
									</span>
									<DownOutlined
										className={cn(
											'mt-[5px] shrink-0 text-[10px] text-character-secondary transition-transform',
											expanded ? 'rotate-180' : null
										)}
									/>
								</button>
								{expanded ? (
									<div className="flex flex-col gap-xs px-xs pb-xs">
										<OscarApprovalArgs args={approval.args} labels={approval.labels} />
										{!approval.resolved && onResolve ? (
											<div className="flex items-center justify-end gap-xs">
												<OWButton
													disabled={!!lockedIds[approval.id] || bulkInFlight}
													onClick={() => void resolveOne(approval.id, false)}
												>
													Reject
												</OWButton>
												<OWButton
													type="primary"
													danger={approval.destructive}
													disabled={!!lockedIds[approval.id] || bulkInFlight}
													onClick={() => void resolveOne(approval.id, true)}
												>
													Approve
												</OWButton>
											</div>
										) : null}
									</div>
								) : null}
							</li>
						);
					})}
				</ul>
			) : null}

			{writes.length > BATCH_ROW_CAP ? (
				<Button
					type="link"
					className="self-start !px-0"
					onClick={() => setShowAll((current) => !current)}
				>
					{showAll ? 'Show fewer' : `Show all ${writes.length}`}
				</Button>
			) : null}

			{pendingWrites.length ? (
				confirmingDestructive && destructiveTargets.length ? (
					<div className="flex flex-col gap-xs">
						<span className="bodyregular text-character-danger">
							{`${plural(
								destructiveTargets.length,
								'of these is',
								'of these are'
							)} destructive. Approve all ${bulkTargets.length} anyway?`}
						</span>
						<div className="flex items-center justify-end gap-xs">
							<Button size="large" onClick={() => setConfirmingDestructive(false)}>
								Cancel
							</Button>
							<OWButton size="large" type="primary" danger onClick={() => resolveAll(true)}>
								{`Yes, approve all ${bulkTargets.length}`}
							</OWButton>
						</div>
					</div>
				) : (
					<div className="flex items-center justify-end gap-xs">
						<OWButton
							size="large"
							disabled={!complete || bulkInFlight || !bulkTargets.length}
							onClick={() => resolveAll(false)}
						>
							Deny all
						</OWButton>
						<OWButton
							size="large"
							type="primary"
							danger={destructiveTargets.length > 0}
							loading={bulkInFlight}
							disabled={!complete || !bulkTargets.length}
							onClick={approveAll}
						>
							{`Approve all ${bulkTargets.length}`}
						</OWButton>
					</div>
				)
			) : null}

			{/* Each request to the OpenWrench team is its own decision. */}
			{teamRequests.map((approval) => (
				<OscarTeamRequestCard
					key={approval.id}
					args={approval.args}
					resolved={approval.resolved}
					approved={approval.approved}
					reason={approval.reason}
					onResolve={onResolve ? (approved) => resolveOne(approval.id, approved) : undefined}
					locked={!!lockedIds[approval.id]}
				/>
			))}
		</div>
	);
};

export default OscarApprovalBatchCard;
